const { useState } = require('react');
const { default: Styled } = require('styled-components');
const { default: Button } = require('@mui/material/Button');
const { default: TextField } = require('@mui/material/TextField');
const Avatar = require('./Avatar');
const ArtworkImages = require('./ArtworkImages');
const SocialActivity = require('./SocialActivity');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Close } = require('@mui/icons-material/Close');
const { runProfanityCheck } = require('utils/profanityFilter');
const InappropriateLanguageDialog = require('routes/app/components/Chat/InappropriateLanguageDialog');

const internals = {};

exports.PeerGameTools = function PeerGameTools({ games }) {

    if (!games.enabled || games.dialogOpen) { return null; }
    const open = ['invited', 'active', 'paused'].includes(games.session?.status);
    const { Tools } = internals;
    return <Tools><Button onClick={() => games.openGame(open ? games.session.id : null)}>{open ? 'Continue game' : 'Play together'}</Button></Tools>;
};

exports.PeerGameSummary = function PeerGameSummary({ event, games, userId, usersNameMap = {} }) {

    const { Summary } = internals;
    const [resultsOpen, setResultsOpen] = useState(false);
    const game = event.content;
    const inviter = game.inviterId === userId ? 'You' : usersNameMap[game.inviterId] || 'Your peer';
    const terminal = ['complete', 'declined', 'cancelled', 'ended'].includes(game.status);
    const action = game.status === 'invited' && game.inviterId === userId ? 'View status'
        : game.status === 'invited' ? 'View invitation' : game.status === 'complete' ? 'View results'
            : terminal ? 'View details' : 'Continue game';
    const complete = game.status === 'complete';
    const ended = ['cancelled', 'ended'].includes(game.status);
    const statistics = game.statistics || {};
    const earned = game.rewards?.earned?.[userId] || 0;
    return <Summary aria-label='Shared game'>
        <strong>{game.game.title}</strong>
        <p>{game.status === 'invited' ? `${inviter} invited ${game.inviterId === userId ? 'your peer' : 'you'} to play together.` : `Shared game · ${game.status}`}</p>
        {terminal && action && <Button onClick={() => setResultsOpen((open) => !open)} aria-expanded={resultsOpen}>{action}</Button>}
        {!terminal && action && <Button onClick={() => games.openGame(event.sessionId)}>{action}</Button>}
        {terminal && !complete && resultsOpen && <p>{game.status === 'declined'
            ? 'Invitation declined. No points earned.'
            : ended && (game.statistics || game.rewards) ? `${game.status === 'cancelled' ? 'Game cancelled' : 'Game ended'}: ${earned} point${earned === 1 ? '' : 's'} earned, ${statistics.correct || 0} of ${statistics.questionCount || game.questionCount || 0} correct.`
                : `${game.status === 'cancelled' ? 'Game cancelled' : 'Game ended before results were recorded.'}`}</p>}
        {complete && resultsOpen && <p>Finished together: {earned} point{earned === 1 ? '' : 's'} earned, {statistics.correct || 0} of {statistics.questionCount || game.questionCount || 0} correct.</p>}
    </Summary>;
};

exports.PeerGamesDialog = function PeerGamesDialog({ games, userId, usersNameMap, useProfanityFilter }) {

    const { PeerGameEvent } = exports;
    const { GamePanel, GamePanelHeader, GamePanelContent, GamePanelTabs } = internals;
    const [key, setKey] = useState('');
    if (!games.enabled || !games.dialogOpen) { return null; }
    const id = games.selectedSessionId;
    const event = [...games.events].reverse().find((e) => e.sessionId === id);
    const current = id && games.session?.id === id ? games.session : event?.content;
    const latestEvent = event && { ...event, version: current.version, content: current };
    const open = ['invited', 'active', 'paused'].includes(games.session?.status);
    return <GamePanel role='region' aria-labelledby='peer-game-heading'>
        <GamePanelHeader id='peer-game-heading'>
            <span>{current ? current.game.kind === 'top-three' ? current.game.title : `${current.game.title} · ${current.questionNumber} of ${current.questionCount}` : 'Play together'}</span>
            <GamePanelTabs aria-label='Game workspace view'>
                <Button size='small' variant='contained' disabled>Game</Button>
                <Button size='small' onClick={games.closeGame}>Chat</Button>
            </GamePanelTabs>
            <IconButton aria-label='Close game and return to chat' onClick={games.closeGame}><Close /></IconButton>
        </GamePanelHeader>
        <GamePanelContent>
            <p>{games.rewards?.enabled
                ? 'Earn points and medals by playing together.'
                : 'Play together for fun; points and medals are currently off.'}</p>
            {current && latestEvent ? <PeerGameEvent key={`${current.id}:${current.questionNumber}`} event={latestEvent} games={games} userId={userId} usersNameMap={usersNameMap} useProfanityFilter={useProfanityFilter} /> : <>
                {open ? <Button onClick={() => games.openGame(games.session.id)}>Open current game</Button> : <>
                    <TextField select fullWidth value={key} onChange={(e) => setKey(e.target.value)} SelectProps={{ native: true, inputProps: { 'aria-label': 'Choose a game' } }} disabled={games.busy || games.uncertain}>
                        <option value='' disabled>Choose a game</option>
                        {(games.games || []).map((g) => <option key={g.key} value={g.key}>{g.title}</option>)}
                    </TextField>
                    <Button disabled={games.busy || games.uncertain || !key} onClick={() => games.invite(key)}>Invite peer to play</Button>
                </>}
                {games.before && <Button disabled={games.busy} onClick={games.older}>Earlier game activity</Button>}
                {games.error && <p role='status'>{games.error} <Button onClick={games.retry}>Retry game action</Button></p>}
            </>}
        </GamePanelContent>
    </GamePanel>;
};

exports.PeerGameEvent = function PeerGameEvent({ event, games, userId, usersNameMap = {}, useProfanityFilter }) {

    const [guess, setGuess] = useState('');
    const [blocked, setBlocked] = useState(null);
    const { Card, Prompt, Controls } = internals;
    const snapshot = event.content;
    const session = games.session;
    const latest = session?.id === event.sessionId && session.version === event.version;
    const current = latest ? session : snapshot;
    const name = games.name || 'Nerpy';
    const q = current.question;
    const social = current.game.kind === 'top-three';
    const actor = usersNameMap[event.actorId] || (event.actorId === userId ? 'You' : 'Your peer');
    const disabled = games.busy || games.uncertain;
    const submit = (e) => {

        e.preventDefault();
        if (!guess.trim() || disabled) { return; }
        const check = useProfanityFilter ? runProfanityCheck(guess) : { passesCheck: true };
        if (!check.passesCheck) { setBlocked(check.badPhrases); return; }
        games.action('guess', guess.trim());
    };

    return <Card aria-label={`${name} game activity`}>
        <header><Avatar name={name} /><div><strong>{name} · {snapshot.game.title}</strong>
            <small>{snapshot.status === 'invited' ? 'Play together' : `${snapshot.questionNumber} of ${snapshot.questionCount}`}</small></div></header>
        <p>{internals.description(event, actor)}</p>
        {event.kind === 'accept' && snapshot.game.introduction && <p>{internals.brand(snapshot.game.introduction, name)}</p>}
        {(event.kind === 'accept' || event.kind === 'next' || (latest && session.status !== 'invited')) && q && <>
            {q.clue && <p><strong>Your clue:</strong> {q.clue}</p>}
            {q.options && <p>Choices: {q.options.join(' · ')}</p>}
            {q.emojiClue ? <Prompt role='img' aria-label={q.accessiblePrompt}>{Array.from(new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(q.emojiClue.replace(/\s/gu, ''))).map((s, i) => <span key={i}>{s.segment}</span>)}</Prompt> : <p>{q.prompt}</p>}
            {q.imageClue && <img src={ArtworkImages[q.imageClue.url] || q.imageClue.url} alt={q.imageClue.alt} referrerPolicy='no-referrer' onError={(e) => { e.currentTarget.alt = 'Artwork could not load. Please try again or choose another question.'; }} />}
        </>}
        {latest && q && <>
            {q.hints.map((hint, i) => <p key={i}><strong>Hint {i + 1}:</strong> {hint}</p>)}
            {q.answer && <p><strong>{q.answer}</strong>{q.explanation && <> — {q.explanation}</>}</p>}
        </>}
        {!latest && snapshot.question?.answer && <p><strong>{snapshot.question.answer}</strong>{snapshot.question.explanation && <> — {snapshot.question.explanation}</>}</p>}
        {latest && session.status === 'invited' && <Controls>
            {session.inviteeId === userId ? <><Button disabled={disabled} variant='contained' onClick={() => games.action('accept')}>Accept and play</Button><Button disabled={disabled} onClick={() => games.action('decline')}>No thanks</Button></>
                : <><span>Waiting for your peer. Keep chatting in the meantime.</span><Button disabled={disabled} onClick={() => { games.action('end'); games.closeGame(); }}>Cancel invitation</Button></>}
        </Controls>}
        {latest && session.status === 'active' && <>
            {social && current.activity && <SocialActivity activity={current.activity} games={games} userId={userId} usersNameMap={usersNameMap} useProfanityFilter={useProfanityFilter} />}
            {!social && q?.status === 'active' && <form onSubmit={submit}>
                <TextField label='Team answer' value={guess} onChange={(e) => setGuess(e.target.value)} disabled={disabled} inputProps={{ maxLength: 200 }} fullWidth />
                <Controls><Button type='submit' variant='contained' disabled={disabled || !guess.trim()}>Submit team answer</Button>
                    <Button disabled={disabled || !q.hintsAvailable} onClick={() => games.action('hint')}>Hint</Button>
                    <Button disabled={disabled} onClick={() => games.action('reveal')}>Reveal answer</Button></Controls>
            </form>}
            {q && q.status !== 'active' && <Button disabled={disabled} onClick={() => games.action('next')}>{session.questionNumber === session.questionCount ? 'Finish game' : 'Next question'}</Button>}
            {!q && <p>This question is unavailable. Leave this game and choose another later.</p>}
            <Controls><Button disabled={disabled} onClick={() => { games.action('pause'); games.closeGame(); }}>Leave game</Button></Controls>
        </>}
        {latest && session.status === 'paused' && <Controls><Button disabled={disabled} onClick={() => games.action('resume')}>Resume game</Button></Controls>}
        {latest && !social && session.status !== 'invited' && <small>{session.statistics.correct} of {session.questionCount} correct · {session.statistics.hintsUsed} hints · Team score {session.statistics.teamScore}</small>}
        {current.status !== 'invited' && <p>Your points earned in this game: {current.rewards?.earned?.[userId] || 0}</p>}
        {latest && games.error && <p role='status'>{games.error} <Button disabled={games.busy} onClick={games.retry}>Retry game action</Button></p>}
        <InappropriateLanguageDialog open={blocked !== null} words={blocked || []} onClose={() => setBlocked(null)} />
    </Card>;
};

internals.brand = (text, name) => text.replace(/\bNerpy\b/g, () => name);
internals.description = (event, actor) => {

    const { kind, content } = event;
    if (kind === 'contribute') { return `${actor} shared their choices. Participation counts; agreement is optional.`; }
    if (kind === 'propose') { return `${actor} suggested a shared list.`; }
    if (kind === 'approve') { return `${actor} approved the shared list.`; }
    if (kind === 'guess') { return `${actor} submitted “${content.guess || 'an answer'}”. ${content.correct ? 'You got it together!' : 'Not quite—keep discussing, or try a hint.'}`; }
    return { invited: `${actor} invited you to solve a puzzle together.`, accept: `${actor} accepted. Talk it over; submit only when you’re ready.`, decline: `${actor} declined the invitation.`,
        hint: `${actor} asked for a hint.`, reveal: `${actor} revealed the answer.`, pause: `${actor} paused the game. Your chat is still open.`, resume: `${actor} resumed the game.`,
        end: `${actor} ended the game.`, next: content.status === 'complete' ? 'You finished the quiz together!' : 'Here’s your next puzzle.' }[kind] || 'Game updated.';
};

internals.Tools = Styled.div`display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px;`;
internals.Card = Styled.article`
    margin: 12px 16px; padding: 16px; border: 1px solid ${({ theme }) => theme.palette?.divider || '#ddd'}; border-radius: 16px;
    header { display: flex; align-items: center; gap: 8px; }
    small { display: block; }
    img { max-width: 100%; max-height: 240px; object-fit: contain; }
    p { white-space: pre-wrap; overflow-wrap: anywhere; }
`;
internals.Prompt = Styled.div`display: flex; flex-wrap: wrap; gap: 12px; font-size: 40px; padding: 12px 0;`;
internals.Controls = Styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0;`;
internals.GamePanel = Styled.aside`
    position: fixed; top: 0; right: 0; bottom: 0; z-index: 4; width: min(520px, 50vw);
    background: ${({ theme }) => theme.palette?.background?.paper || '#fff'}; border-left: 1px solid ${({ theme }) => theme.palette?.divider || '#ddd'};
    display: flex; flex-direction: column; box-sizing: border-box;
    @media (max-width: 600px) { width: 100%; border-left: 0; }
`;
internals.GamePanelHeader = Styled.header`
    min-height: 64px; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid ${({ theme }) => theme.palette?.divider || '#ddd'}; font-weight: 700; gap: 12px;
`;
internals.GamePanelTabs = Styled.nav`
    display: none;
    @media (max-width: 600px) { display: flex; gap: 4px; margin-left: auto; }
`;
internals.GamePanelContent = Styled.div`
    overflow-y: auto; flex: 1; padding: 16px;
    p { overflow-wrap: anywhere; }
    .MuiFormControl-root { margin-top: 12px; }
`;

// Virtua temporarily disables pointer events on the scrolling list. Keep these
// explicit controls usable, like the interactive controls in normal chat items.
internals.Summary = Styled.article`pointer-events: auto; margin: 12px 16px; padding: 12px; border: 1px solid #ddd; border-radius: 12px; p { margin: 4px 0; }`;
